import { ActionReducerMapBuilder, createSlice } from '@reduxjs/toolkit';
// eslint-disable-next-line import/no-cycle
import {
  createDefaultDescriptionPayload,
  defaultDescriptionsParams,
  updateDefaultDescriptionsParams,
} from '../models/payload/defaultDescriptions';
import { DefaultDescriptionsResponse } from '../models/response/defaultDescription';
import {
  createDefaultDescriptionService,
  getDefaultDescriptions,
  removeDefaultDescription,
  updateDefaultDescription,
} from '../services/defaultDescriptions';
import NUMBER_TABLE_PER_PAGE from '../shared/constants/numberPerPage';

// eslint-disable-next-line import/no-cycle
import { createAppAsyncThunk } from '../utils/reduxUtil';

interface DefaultDescriptionsStateInterface {
  loading: boolean;
  pageNumber: number;
  search: string;
  defaultDescriptions: DefaultDescriptionsResponse | undefined;
}

const initialState: DefaultDescriptionsStateInterface = {
  loading: false,
  defaultDescriptions: undefined,
  pageNumber: 1,
  search: '',
};

const getVendorDefaultDescriptions = createAppAsyncThunk<DefaultDescriptionsResponse, defaultDescriptionsParams>(
  'defaultDescriptions/getVendorDefaultDescriptions',
  async (params) => {
    const response = await getDefaultDescriptions(params);
    return response;
  }
);

const createDefaultDescription = createAppAsyncThunk<void, createDefaultDescriptionPayload>(
  'defaultDescriptions/createVendorDefaultDescriptions',
  async (payload, { dispatch, getState }) => {
    const { pageNumber, search } = getState().defaultDescriptions;
    await createDefaultDescriptionService(payload);
    await dispatch(
      getVendorDefaultDescriptions({ PageNumber: pageNumber, Search: search, PageSize: NUMBER_TABLE_PER_PAGE })
    );
  }
);

const updateDefaultDescriptionById = createAppAsyncThunk<void, updateDefaultDescriptionsParams>(
  'defaultDescriptions/updateVendorDefaultDescriptions',
  async (payload, { dispatch, getState }) => {
    const { pageNumber, search } = getState().defaultDescriptions;
    const response = await updateDefaultDescription(payload);

    await dispatch(
      getVendorDefaultDescriptions({ PageNumber: pageNumber, Search: search, PageSize: NUMBER_TABLE_PER_PAGE })
    );

    return response;
  }
);

const deleteDefaultDescriptionById = createAppAsyncThunk<void, number>(
  'defaultDescriptions/deleteVendorDefaultDescriptions',
  async (id, { dispatch, getState }) => {
    const { pageNumber, search } = getState().defaultDescriptions;
    await removeDefaultDescription(id);
    await dispatch(
      getVendorDefaultDescriptions({ PageNumber: pageNumber, Search: search, PageSize: NUMBER_TABLE_PER_PAGE })
    );
  }
);

const defaultDescriptionsSlice = createSlice({
  name: 'defaultDescriptions',
  initialState,
  reducers: {
    setPageNumber: (state, action) => ({
      ...state,
      pageNumber: action.payload,
    }),
    setSearch: (state, action) => ({
      ...state,
      search: action.payload,
    }),
  },
  extraReducers: (builder: ActionReducerMapBuilder<DefaultDescriptionsStateInterface>) => {
    builder.addCase(getVendorDefaultDescriptions.pending, (state) => ({
      ...state,
      loading: true,
    }));

    builder.addCase(getVendorDefaultDescriptions.fulfilled, (state, action) => ({
      ...state,
      loading: false,
      defaultDescriptions: action.payload,
    }));

    builder.addCase(createDefaultDescription.pending, (state) => ({
      ...state,
      loading: true,
    }));

    builder.addCase(updateDefaultDescriptionById.pending, (state) => ({
      ...state,
      loading: true,
    }));

    builder.addCase(deleteDefaultDescriptionById.pending, (state) => ({
      ...state,
      loading: true,
    }));

    builder.addMatcher(
      (action) =>
        [
          getVendorDefaultDescriptions.rejected,
          createDefaultDescription.rejected,
          updateDefaultDescriptionById.rejected,
          deleteDefaultDescriptionById.rejected,
        ].includes(action.type),
      (state) => ({
        ...state,
        loading: false,
      })
    );
  },
});

export default defaultDescriptionsSlice;
export {
  createDefaultDescription,
  deleteDefaultDescriptionById,
  getVendorDefaultDescriptions,
  updateDefaultDescriptionById,
};
